import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "~/lib/ui/utils/utils";

const alertVariants = cva(
  `
    relative w-full rounded-lg border bg-card p-4

    [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground

    [&>svg+div]:translate-y-[-3px]

    [&>svg~*]:pl-7
  `,
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "text-foreground",
        destructive: cn(`
          border-detail-failure/50 text-detail-failure

          [&>svg]:text-detail-failure

          dark:border-detail-failure
        `),
        info: cn(`
          border-detail-brand text-foreground

          dark:border-detail-brand
        `),
        warning: cn(`
          border-detail-warning text-detail-warning

          dark:border-detail-warning
        `),
      },
    },
  },
);

type AlertVariantProp = VariantProps<typeof alertVariants>["variant"];

function AlertBase({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      role="alert"
      {...props}
    />
  );
}

function AlertTitle({
  children,
  className,
  ...props
}: React.ComponentProps<"h5">) {
  return (
    <h5
      className={cn("mb-1 font-medium leading-none tracking-tight", className)}
      {...props}
    >
      {children}
    </h5>
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        `
          text-sm

          [&_p]:leading-relaxed
        `,
        className,
      )}
      {...props}
    />
  );
}

type AlertProps = React.ComponentProps<typeof AlertBase> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  variant?: AlertVariantProp;
};

function Alert({
  children,
  description,
  icon,
  title,
  variant,
  ...props
}: AlertProps) {
  return (
    <AlertBase {...props} variant={variant}>
      {icon}
      {title && <AlertTitle>{title}</AlertTitle>}
      {description && <AlertDescription>{description}</AlertDescription>}
      {children}
    </AlertBase>
  );
}

export { Alert, AlertTitle, AlertDescription };
